import { useCallback, useEffect, useRef, useState } from 'react';
import { getConvertedTimeToUTC, getTimeZone, highchartsCredits, stringFormatting } from "./utils";
import Helper from "./Helper";
import { CHART_COLOURS_WAITS, OTHER_COLOUR_CODE, TARGET_COLOUR_CODES } from "../component/Constants";
import { InstanceOptions } from "../container/comparison/activity-comparison/types";
import dayjs, { Dayjs } from "dayjs";
import CombinedChanges from "../types/instance/CombinedChanges";
import ChartPlotLine from "../types/ChartPlotLine";

const UPMOST_CHART_INDEX_VALUE = 99999

const useChartOptions = (chartData: any, instanceOptions: InstanceOptions[], options: any, generateId: number, setStatementColors?: Function, setWaitsColors?: Function) => {

    const [chartOptions, setChartOptions] = useState<any[]>([{}, {}]);

    const metric = options.metric
    let peakTimeSeries1 = _getPeekTime(chartData[0], metric);
    let peakTimeSeries2 = _getPeekTime(chartData[1], metric);

    // Use `useRef` to maintain a stable reference for statementColors
    const statementColors = useRef<{ [key: string]: string }>({});
    const waitsColors = useRef<{ [key: string]: string }>({});

    const getColourForStatement = useCallback((key: string) => {

        if (options.type === 'waits') {

            if (!waitsColors.current[key]) {
                const colorIndex = Object.keys(waitsColors.current).length % CHART_COLOURS_WAITS.length;
                waitsColors.current[key] = CHART_COLOURS_WAITS[colorIndex];

                // Update setStatementColors if it's provided
                if (setWaitsColors) {
                    setWaitsColors(waitsColors.current);
                }
            }
            return waitsColors.current[key];

        } else {
            if (!statementColors.current[key]) {
                const colorIndex = Object.keys(statementColors.current).length % TARGET_COLOUR_CODES.length;
                statementColors.current[key] = TARGET_COLOUR_CODES[colorIndex];

                // Update setStatementColors if it's provided
                if (setStatementColors) {
                    setStatementColors(statementColors.current);
                }
            }
            return statementColors.current[key];
        }

    }, [setStatementColors]);

    const tooltip = {
        ...(options.tooltip || {}),
        ...(options.tooltip ? {
            borderWidth: 0,
            outside: true,
            padding: 0,
            shadow: false,
            shared: true,
            useHTML: true,
            className: 'highcharts-tooltip-single',
            backgroundColor: null,
            borderRadius: 0,
            borderColor: 'none'
        } : {
            shared: true,
            valueDecimals: 0,
            className: 'highcharts-tooltip-single',
            backgroundColor: null,
            borderRadius: 0,
            borderColor: 'none'
        })
    };

    const getConvertedData = (list: { values: [] }, metric: string, interval: number, start: number, end: number) => {
        const fullRange = [];
        for (let time = start; time <= end; time += interval) {
            fullRange.push(time);
        }

        const valueMap = new Map(list.values.map(item => [Math.floor(getConvertedTimeToUTC(item) / interval) * interval, item]));

        // Fill with values from `valueMap` or 0 for missing data points
        return fullRange.map((timestamp) => {
            const roundedTimestamp = Math.floor(timestamp / interval) * interval;
            const point = valueMap.get(roundedTimestamp);
            return point ? [timestamp, point[metric]] : [timestamp, 0]; // Use 0 for missing data points
        });
    };

    const getSeries = (data: any, index: number) => {
        // If `data` is empty, create a single series with 0 values across the time range
        if (!Object.keys(data).length) {
            return [{
                name: 'No Data Available',
                id: metric,
                type: options.chart,
                data: getConvertedData(
                    { values: [] }, // Pass an empty values array
                    metric,
                    options.interval || 3600000,
                    instanceOptions[index].periodStart.valueOf(),
                    instanceOptions[index].periodEnd.valueOf()
                ),
            }];
        }

        // Otherwise, process `data` normally
        return Object.keys(data).map((key: string) => {
            return {
                name: key,
                id: key,
                type: options.chart,
                data: getConvertedData(
                    chartData[index][key],
                    metric,
                    options.interval || 3600000,
                    instanceOptions[index].periodStart.valueOf(),
                    instanceOptions[index].periodEnd.valueOf()
                ),
                zIndex: 1,
                color: key === 'Other SQL' ? OTHER_COLOUR_CODE : getColourForStatement(key),
                legendIndex: chartData[index][key].orderIndex,
                index: (UPMOST_CHART_INDEX_VALUE - chartData[index][key].orderIndex),
                boostThreshold: 0,
            };
        });
    };

    const getYAxisFormat = (val: number) => {
        const isTime = metric.includes('time') || metric === 'duration'
        return isTime ? Helper.getTimeInEnglish(val) : val
    }

    useEffect(() => {
        if (Object.keys(chartData).length !== instanceOptions.length) {
            return;
        }

        const optionsToSet: any[] = [];

        instanceOptions.forEach((_, index: number) => {
            let plotLines: any[] = []
            if (options?.combinedChanges?.length) {
                const tz = getTimeZone();
                const periodFrom = tz === 'UTC' ? dayjs.utc(instanceOptions[index].periodStart) : dayjs(instanceOptions[index].periodStart);
                const periodTo = tz === 'UTC' ? dayjs.utc(instanceOptions[index].periodEnd) : dayjs(instanceOptions[index].periodEnd);
                plotLines = _getPlotBandAndPlotLines(options.combinedChanges[index], periodFrom, periodTo, options.interval)
                if (plotLines.length) {
                    plotLines = plotLines.map((chartPlotLine: ChartPlotLine) => {
                        return {
                            ...chartPlotLine,
                            value: chartPlotLine.timeValue
                        }
                    })
                }
            }

            const series = getSeries(chartData[index], index)

            // For each chart, apply the correct min and max from minMax
            optionsToSet.push({
                chart: {
                    height: options.height + 80,
                    marginBottom: 130,
                    type: options.chart,
                    zoomType: '',
                    style: {
                        fontFamily: 'system-ui, Arial, sans-serif',
                        letterSpacing: 'normal',
                    },
                },
                title: { text: '' },
                xAxis: [{
                    endOnTick: false,
                    showFirstLabel: true,
                    plotLines: plotLines,
                    showLastLabel: true,
                    tickInterval: options.interval || 3600000,
                    type: 'datetime',
                    lineColor: '#CCCCCC',
                    tickColor: '#CCCCCC',
                    min: instanceOptions[index].periodStart.valueOf(),
                    max: instanceOptions[index].periodEnd.valueOf(),
                    labels: {
                        distance: 9,
                        style: {
                            color: '#666666',
                            cursor: 'default',
                            fontSize: '11px',
                            letterSpacing: '1px',
                        },
                    },
                    crosshair: {
                        width: 15,
                        color: "rgba(125, 160, 200, 0.15)",
                    },
                }],
                time: {
                    useUTC: getTimeZone() === 'UTC',
                },
                yAxis: [{
                    labels: {
                        enabled: true,
                        formatter: function (): string {
                            // @ts-ignore
                            return getYAxisFormat(this.value);
                        },
                    },
                    crosshair: true,
                    title: {
                        text: `${stringFormatting(metric).replace(/_/gi, ' ')}`,
                    },
                    opposite: index === 1,
                    max:  Math.max(peakTimeSeries1, peakTimeSeries2),
                }],
                plotOptions: {
                    animation: false,
                    column: {
                        stacking: 'normal',
                        minPointLength: options.minHeight || 0,
                        pointPlacement: "on",
                        pointPadding: 0.1,
                        groupPadding: 0.1,
                        borderWidth: 0,
                    },
                    area: { stacking: 'normal' },
                    series: { marker: { enabled: false }, pointPlacement: "on" },
                },
                tooltip: tooltip,
                legend: {
                    align: 'center',
                    verticalAlign: 'bottom',
                    maxHeight: 60,
                    layout: 'horizontal',
                    padding: 0,
                    x: 0,
                    y: 0,
                    floating: true,
                    backgroundColor: 'transparent',
                    enabled: true,
                    itemStyle: {
                        fontSize: '12px',
                        fontWeight: 'bold',
                        fontFamily: 'system-ui, Arial, sans-serif',
                    },
                    navigation: {
                        enabled: true,
                        style: {
                            fontSize: '10px',
                        },
                    },
                },
                series: series,
                exporting: { enabled: false },
                credits: { enabled: highchartsCredits(options.licences) },
                accessibility: {
                    enabled: false,
                },
            });
        });
        setChartOptions(optionsToSet);
    }, [chartData, generateId]);

    return chartOptions;
};

const _getPlotBandAndPlotLines = (combinedChanges: [], from: Dayjs, to: Dayjs, interval: number) => {
    const chartCategories = Helper.getChartAxisCategories(from, to, interval)
    const formatChanges = transformData(combinedChanges)

    // No need for period(first param) since the 'see more' only expand / collapse section
    return Helper.getChartPlotLines(null, formatChanges, chartCategories, interval, true);
}

function transformData(originalData: any) {
    let combinedChanges: CombinedChanges[] = []

    for (let index = 0; index < originalData.length; index++) {
        const currentItem = originalData[index];

        combinedChanges.push({
            id: index,
            instanceId: currentItem.datasourceid,
            timeslice: currentItem.timeslice,
            [currentItem.event]: currentItem
        });
    }
    return combinedChanges
}

const _getPeekTime = (data: any, metric: string) => {
    if (!data || !Object.keys(data).length) {
        return 0;
    }

    const timesliceMap: Record<string, number> = {};

    Object.values(data).forEach((item: any) => {
        if (item.values) {
            item.values.forEach((value: any) => {
                const { timeslice } = value;

                // Dynamically access the metric value
                const metricValue = value[metric];

                if (metricValue !== undefined) {
                    // Aggregate waittimes for each timeslice
                    if (timesliceMap[timeslice]) {
                        timesliceMap[timeslice] += metricValue;
                    } else {
                        timesliceMap[timeslice] = metricValue;
                    }
                }
            });
        }
    });

    let max = 0;
    for (const aggregatedWaittime of Object.values(timesliceMap)) {
        if (aggregatedWaittime > max) {
            max = aggregatedWaittime;
        }
    }
    return max;
};

export default useChartOptions;
